import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlogPost } from '../../lib/supabase';

interface TabSectionProps {
  articles: BlogPost[];
  loading: boolean;
}

export default function TabSection({ articles, loading }: TabSectionProps) {
  const [activeTab, setActiveTab] = useState('新着記事');

  const tabs = ['新着記事', 'おすすめ記事', '人気記事'];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.');
  };

  // タブに応じて記事をフィルタリング
  const getFilteredArticles = () => {
    if (activeTab === '新着記事') {
      return articles.slice(0, 9);
    } else if (activeTab === 'おすすめ記事') {
      // おすすめ記事は sort_order が低い順
      return [...articles].sort((a, b) => a.sort_order - b.sort_order).slice(0, 9);
    } else {
      // 人気記事は公開日が新しい順
      return [...articles].sort((a, b) => 
        new Date(b.published_date).getTime() - new Date(a.published_date).getTime()
      ).slice(0, 9);
    }
  };

  const filteredArticles = getFilteredArticles();

  return (
    <section>
      {/* タブナビゲーション */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab
                ? 'text-cyan-500 border-b-2 border-cyan-500'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ローディング */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-cyan-500">
            <i className="ri-loader-4-line text-5xl animate-spin"></i>
          </div>
          <p className="text-gray-500">読み込み中...</p>
        </div>
      ) : filteredArticles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => (
            <article
              key={article.id}
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group"
            >
              <Link to={`/blog/${article.slug}`} className="block cursor-pointer">
                <div className="relative">
                  <div className="w-full h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-cyan-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                      {article.category}
                    </span>
                  </div>
                  {article.video_url && (
                    <div className="absolute top-3 right-3">
                      <span className="bg-red-500 text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                        <i className="ri-play-circle-line"></i>
                        動画あり
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <time className="text-gray-500 text-sm">{formatDate(article.published_date)}</time>
                  <h2 className="text-gray-900 font-semibold text-lg leading-tight mt-2 mb-3 group-hover:text-cyan-500 transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-4 flex items-center text-cyan-500 text-sm font-medium">
                    続きを読む
                    <i className="ri-arrow-right-line ml-1"></i>
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
            <i className="ri-article-line text-5xl"></i>
          </div>
          <p className="text-gray-500">記事がありません</p>
        </div>
      )}

      {/* もっと見るボタン */}
      {!loading && filteredArticles.length > 0 && (
        <div className="mt-8 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center px-6 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors cursor-pointer font-medium whitespace-nowrap"
          >
            もっと見る
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      )}
    </section>
  );
}
